import type { DataFeed } from '../types/datafeed'
import { MultiPeriodCache } from './MultiPeriodCache'

/**
 * LRU cap for a SHARED multi-period (D/W/M) preload cache. Sized to span a large
 * grid of distinct-ticker cells (e.g. 4×4) plus a drill overlay and a second
 * chart panel, all built on one datafeed; beyond it the LRU evicts the
 * least-recently-used ticker. Far above MultiPeriodCache's per-chart default of
 * 3, which would thrash under grid-wide sharing.
 */
export const SHARED_MAX_TICKERS = 32

/**
 * One shared MultiPeriodCache per datafeed instance. Keyed by datafeed identity
 * (a WeakMap) rather than exposed on the datafeed itself, so the cache lives
 * entirely inside charts-core — the frontend `EdgeDataFeed` never has to
 * value-import MultiPeriodCache from the `@edge/charts` barrel, which would drag
 * EdgeChart's klinecharts register*() side effects into the datafeed module.
 * The cache is GC'd together with its datafeed (workspace lifetime).
 */
const caches = new WeakMap<DataFeed, MultiPeriodCache>()

/**
 * The multi-period cache shared by every ChartManager built on `datafeed` — so
 * cells reuse each other's prefetched bars and a remount keeps them instead of
 * re-fetching. Lazily created on first use per datafeed.
 */
export function getSharedMultiPeriodCache(datafeed: DataFeed): MultiPeriodCache {
  let cache = caches.get(datafeed)
  if (!cache) {
    cache = new MultiPeriodCache(SHARED_MAX_TICKERS)
    caches.set(datafeed, cache)
  }
  return cache
}
